import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { ProfessionalsService } from '@/services/professional.service';
import { UserService } from '@/services/user.service';
import { clearUserScopedQueryCache } from '@/utils/clearUserScopedQueryCache';
import {
	removeAdminData,
	removeCompanyData,
	setProfessionalData,
	setUserData,
} from '@/utils/cookies';

/**
 * Destino do link "entrar como" do sandbox. O backend já assinou o cookie de
 * sessão daquele papel e redirecionou para cá; esta página faz o que o login
 * faria no cliente (busca o "me", grava os dados locais) e segue para a home.
 */
const SandboxEnterPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [error, setError] = useState<string | null>(null);
	const as = searchParams.get('as');

	useEffect(() => {
		let cancelled = false;
		const enter = async () => {
			try {
				removeAdminData();
				removeCompanyData();
				await clearUserScopedQueryCache(queryClient);
				if (as === 'professional') {
					const me = await ProfessionalsService.getMe();
					if (cancelled) return;
					setProfessionalData(me);
					navigate('/profissional', { replace: true });
					return;
				}
				const me = await UserService.getById({ isMe: true });
				if (cancelled) return;
				setUserData(me);
				navigate('/home', { replace: true });
			} catch {
				if (!cancelled) {
					setError(
						'Não foi possível entrar. O link pode ter expirado ou já ter sido usado — gere outro no sandbox.',
					);
				}
			}
		};
		void enter();
		return () => {
			cancelled = true;
		};
	}, [as, navigate, queryClient]);

	return (
		<section className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
			<div className="w-full max-w-md rounded-[2rem] bg-white p-8 text-center shadow-sm">
				<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-900 text-white">
					<FlaskConical className="h-6 w-6" />
				</span>
				<h1 className="mt-4 text-lg font-black text-stone-900">
					{error ? 'Link inválido' : 'Entrando no sandbox...'}
				</h1>
				<p className="mt-2 text-sm text-stone-400">
					{error ??
						`Preparando a sessão de ${
							as === 'professional' ? 'profissional' : 'usuário'
						} de teste.`}
				</p>
			</div>
		</section>
	);
};

export { SandboxEnterPage };
